import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Loader2, GraduationCap } from "lucide-react";
import { useStudentAuth } from "../StudentAuth";
import { sForgot, sReset } from "../studentApi";
import { toast } from "sonner";

const shell = (title, children) => (
  <div className="flex min-h-screen items-center justify-center bg-navy px-4 py-10" style={{ fontFamily: "Satoshi, sans-serif" }}>
    <div className="w-full max-w-md">
      <div className="mb-6 text-center">
        <span className="font-heading text-4xl font-bold text-white" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>ASAI</span>
        <p className="mt-2 flex items-center justify-center gap-2 text-sm text-slate-400"><GraduationCap className="h-4 w-4 text-orange" /> Student Portal</p>
      </div>
      {children}
      <p className="mt-4 text-center text-xs text-slate-500">{title}</p>
    </div>
  </div>
);

const field = "rounded-lg border border-white/10 bg-slate-800 px-4 py-3 text-white outline-none focus:border-orange";
const card = "flex flex-col gap-4 rounded-2xl border border-white/10 bg-slate-900 p-8";

export function StudentLogin() {
  const { login } = useStudentAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const expired = new URLSearchParams(loc.search).get("expired");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(expired ? "Your session expired. Please log in again." : "");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try { await login(email.trim().toLowerCase(), password); nav("/student/dashboard"); }
    catch (err) { setError(err.message); } finally { setLoading(false); }
  };

  return shell("Learn Today. Lead Tomorrow.", (
    <form onSubmit={submit} data-testid="student-login-form" className={card}>
      <h1 className="text-xl font-semibold text-white">Sign in</h1>
      {error && <div data-testid="student-login-error" className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</div>}
      <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" data-testid="student-email" className={field} />
      <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" data-testid="student-password" className={field} />
      <button type="submit" disabled={loading} data-testid="student-login-submit" className="inline-flex items-center justify-center gap-2 rounded-lg bg-orange px-5 py-3 font-medium text-white disabled:opacity-60">
        {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Signing in…</> : "Sign in"}
      </button>
      <div className="flex justify-between text-sm text-slate-400">
        <Link to="/student/forgot-password" className="hover:text-orange">Forgot password?</Link>
        <Link to="/student/register" className="hover:text-orange" data-testid="link-register">Create account</Link>
      </div>
    </form>
  ));
}

export function StudentRegister() {
  const { register } = useStudentAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ name: "", email: "", mobile: "", college: "", city: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try { await register({ ...f, email: f.email.trim().toLowerCase() }); nav("/student/dashboard"); }
    catch (err) { setError(err.message); } finally { setLoading(false); }
  };

  return shell("Create your student account", (
    <form onSubmit={submit} data-testid="student-register-form" className={card}>
      <h1 className="text-xl font-semibold text-white">Create account</h1>
      {error && <div data-testid="student-register-error" className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</div>}
      <input required value={f.name} onChange={set("name")} placeholder="Full name" data-testid="reg-name" className={field} />
      <input type="email" required value={f.email} onChange={set("email")} placeholder="Email" data-testid="reg-email" className={field} />
      <input required value={f.mobile} onChange={set("mobile")} placeholder="Mobile (10-digit)" data-testid="reg-mobile" className={field} />
      <div className="grid grid-cols-2 gap-3">
        <input value={f.college} onChange={set("college")} placeholder="College" data-testid="reg-college" className={field} />
        <input value={f.city} onChange={set("city")} placeholder="City" data-testid="reg-city" className={field} />
      </div>
      <input type="password" required value={f.password} onChange={set("password")} placeholder="Password (min 8 chars)" data-testid="reg-password" className={field} />
      <button type="submit" disabled={loading} data-testid="student-register-submit" className="inline-flex items-center justify-center gap-2 rounded-lg bg-orange px-5 py-3 font-medium text-white disabled:opacity-60">
        {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Creating…</> : "Create account"}
      </button>
      <Link to="/student/login" className="text-center text-sm text-slate-400 hover:text-orange">Already have an account? Sign in</Link>
    </form>
  ));
}

export function StudentForgot() {
  const [email, setEmail] = useState("");
  const [devToken, setDevToken] = useState("");
  const [newPass, setNewPass] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const request = async (e) => {
    e.preventDefault(); setLoading(true);
    try { const r = await sForgot(email.trim().toLowerCase()); setSent(true); if (r.devToken) setDevToken(r.devToken); toast.success(r.message); }
    catch (err) { toast.error(err.message); } finally { setLoading(false); }
  };
  const reset = async (e) => {
    e.preventDefault(); setLoading(true);
    try { const r = await sReset(devToken, newPass); toast.success(r.message); window.location.href = "/student/login"; }
    catch (err) { toast.error(err.message); } finally { setLoading(false); }
  };

  return shell("Reset your password", (
    <div className={card} data-testid="student-forgot-form">
      <h1 className="text-xl font-semibold text-white">Forgot password</h1>
      {!sent ? (
        <form onSubmit={request} className="flex flex-col gap-4">
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your registered email" data-testid="forgot-email" className={field} />
          <button type="submit" disabled={loading} data-testid="forgot-submit" className="rounded-lg bg-orange px-5 py-3 font-medium text-white disabled:opacity-60">{loading ? "Sending…" : "Send reset link"}</button>
        </form>
      ) : (
        <form onSubmit={reset} className="flex flex-col gap-4">
          <p className="text-sm text-slate-400">Email delivery is not configured in this phase, so use the reset token below to set a new password.</p>
          <input value={devToken} onChange={(e) => setDevToken(e.target.value)} placeholder="Reset token" data-testid="forgot-token" className={field} />
          <input type="password" required value={newPass} onChange={(e) => setNewPass(e.target.value)} placeholder="New password (min 8 chars)" data-testid="forgot-newpass" className={field} />
          <button type="submit" disabled={loading} data-testid="forgot-reset-submit" className="rounded-lg bg-orange px-5 py-3 font-medium text-white disabled:opacity-60">{loading ? "Updating…" : "Set new password"}</button>
        </form>
      )}
      <Link to="/student/login" className="text-center text-sm text-slate-400 hover:text-orange">Back to sign in</Link>
    </div>
  ));
}
